/**
 * FundraiserCard.tsx - Fundraiser Display Card Component
 *
 * This component renders individual fundraiser cards used in dashboard views,
 * gallery displays, and selection interfaces. Provides a compact view of
 * fundraiser information with progress indicators and action buttons.
 *
 * Key Features:
 * - Fundraiser image with fallback handling
 * - Progress bar with percentage and amounts
 * - Status indicators and badges
 * - Responsive design for different container sizes
 * - Click handling for navigation
 * - Hover effects and animations
 *
 * Props:
 * - fundraiser: Fundraiser - The fundraiser data to display
 * - onClick?: () => void - Click handler for card interaction
 * - showActions?: boolean - Whether to show action buttons
 *
 * Dependencies: MobX store, fundraiser utilities, styled-components
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { getPrimaryFundraiserImage } from "../utils/fundraiserUtils";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
  shouldShowPendingBadge,
  getPendingBadgeText,
} from "../utils/badgesUtils";
import { Fundraiser } from "../models/FundraisersGridModels";
import { CreateFundraiserModel } from "../models";

const FundraiserCardContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  max-width: 400px;
  width: 100%;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const CardImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ExpirationBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(220, 38, 38, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
`;

const PendingBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(59, 130, 246, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
`;

const CardContent = styled.div`
  padding: 24px;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 16px 0;
  text-align: center;
  line-height: 1.3;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressSection = styled.div<{ hasGoal: boolean }>`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
`;

const AmountRaised = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #10b981;
  text-align: right;
`;

const DonateButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${(props) =>
    props.disabled ? "#9ca3af" : "linear-gradient(135deg, #10b981, #059669)"};
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: ${(props) => (props.disabled ? "none" : "translateY(-2px)")};
    box-shadow: ${(props) =>
    props.disabled ? "none" : "0 8px 24px rgba(16, 185, 129, 0.4)"};
  }

  &:active {
    transform: ${(props) => (props.disabled ? "none" : "translateY(0)")};
  }
`;

interface FundraiserCardProps {
  fundraiser: CreateFundraiserModel;
  onClick?: () => void;
  isEmbedPreview?: boolean;
}

export const FundraiserCard: React.FC<FundraiserCardProps> = observer(
  ({ fundraiser, onClick, isEmbedPreview = false }) => {
    const [displayedPercentage, setDisplayedPercentage] = useState(0);

    const primaryImageUrl = getPrimaryFundraiserImage(fundraiser);

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (onClick) {
        onClick();
      }
    };

    const handleDonateClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      // Don't allow donation if button is disabled
      if (isDonateDisabled()) {
        return;
      }

      if (onClick) {
        onClick();
      }
    };

    const isDonateDisabled = () => {
      return false;
    };

    const getDonateButtonText = () => {
      return "Donate";
    };

    const hasGoal = fundraiser.goal > 0;
    const progress = hasGoal
      ? Math.min((fundraiser.totalRaisedAmount / fundraiser.goal) * 100, 100)
      : 100;

    // Trigger animation after component mounts
    useEffect(() => {
      const timer = setTimeout(() => {
        setDisplayedPercentage(progress);
      }, 100); // Small delay to ensure component is rendered

      return () => clearTimeout(timer);
    }, [progress]);

    // Use the utility function for consistent image retrieval
    const primaryImage = getPrimaryFundraiserImage(fundraiser);

    // Badge priority logic: Pending takes priority over Expiration
    const shouldShowPendingBadgeForCard = shouldShowPendingBadge(
      fundraiser.startDate,
      fundraiser.status
    );
    const shouldShowExpirationBadgeForCard =
      !shouldShowPendingBadgeForCard &&
      shouldShowExpirationBadge(fundraiser.endDate, fundraiser.status);

    return (
      <FundraiserCardContainer onClick={handleClick}>
        <CardImageContainer>
          {
            primaryImage?.fullURL && <CardImage
              src={primaryImage?.fullURL || ""}
              alt={" "}
            // onError={(e) => {
            //   const target = e.target as HTMLImageElement;
            //   target.src =
            //     "https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?w=400&h=300&fit=crop";
            // }}
            />
          }

          {shouldShowExpirationBadgeForCard && (
            <ExpirationBadge>
              {getExpirationBadgeText(fundraiser.endDate ? new Date(fundraiser.endDate) : new Date())}
            </ExpirationBadge>
          )}
          {shouldShowPendingBadgeForCard && (
            <PendingBadge>
              {getPendingBadgeText(new Date(fundraiser.startDate))}
            </PendingBadge>
          )}
        </CardImageContainer>
        <CardContent>
          <CardTitle>{fundraiser.title || fundraiser.name}</CardTitle>
          <ProgressSection hasGoal={true}>
            <ProgressBarContainer>
              <ProgressBar percentage={displayedPercentage} />
            </ProgressBarContainer>
            <ProgressInfo>
              <ProgressText>
                {hasGoal ? `${progress.toFixed(1)}% of goal` : "Raised to date"}
              </ProgressText>
              <AmountRaised>
                {formatCurrency(fundraiser.totalRaisedAmount)}
              </AmountRaised>
            </ProgressInfo>
          </ProgressSection>
          <DonateButton
            onClick={handleDonateClick}
            disabled={isDonateDisabled()}
          >
            {getDonateButtonText()}
          </DonateButton>
        </CardContent>
      </FundraiserCardContainer>
    );
  }
);
